import { ApiResourceInterface, FileData, MenuItem, WorkspaceContentItem } from '../../interfaces';
import { WorkspaceDetailFileTypes } from '../../../modules/workspace-module/polly-manage-workspace.constants';
import {
  FileType,
  WORKSPACE_CONTENT_ICONS,
  WorkspaceAnalysisContextMenu,
  WorkspaceFilesContextMenu,
  WorkspaceFolderContextMenu,
  ViewOnlyWSItemContextMenu,
  WorkspaceNotebookContextMenu,
} from '../../../polly-manage.constants';
import { RootApiResourceListResponseInterface, ElasticApiResponse, ElasticCountApiResponse, Hit, FILE_UPLOAD_TYPES } from 'polly-commons';
import { throwError } from 'rxjs';
import { WorkspaceContentMetaData, WorkspaceFile, ESWorkspaceAttributes, ReportFileTokenRequestInterface } from '../../interfaces';

/**
 * This function extracts the name, type, date, type of icon it needs and contextmenu according to the file type
 */
export function getFileData(workspaceFile: Hit<WorkspaceContentItem>, isUserViewOnly: boolean = false): FileData | undefined {
  let fileDataObj: FileData | undefined;
  switch (workspaceFile._source.entity_type) {
    case FileType.NOTEBOOK: {
      fileDataObj = setWorkspaceFileInfo(
        workspaceFile._source.entity_name,
        workspaceFile._source.modified_date,
        'Notebook',
        workspaceFile._source.size,
        undefined,
        WORKSPACE_CONTENT_ICONS.notebookIcon,
        isUserViewOnly ? ViewOnlyWSItemContextMenu : WorkspaceNotebookContextMenu,
        (workspaceFile._source.links.self) || undefined,
      );
      break;
    }
    case FileType.FOLDER: {
      fileDataObj = setWorkspaceFileInfo(
        workspaceFile._source.entity_name,
        undefined,
        'Folder',
        workspaceFile._source.size,
        undefined,
        WORKSPACE_CONTENT_ICONS.folderIcon,
        isUserViewOnly ? [] : WorkspaceFolderContextMenu,
        (workspaceFile._source.links.self) || undefined,
      );
      break;
    }
    case FileType.FILE: {
      const fileType: string = workspaceFile._source.entity_name.endsWith('.ipynb') ? 'Notebook' : 'File';
      const fileIcon: string = fileType === 'Notebook' ? WORKSPACE_CONTENT_ICONS.notebookIcon : WORKSPACE_CONTENT_ICONS.fileIcon;
      const wsContextMenu: MenuItem[] = workspaceFile._source.entity_name.endsWith('.ipynb')
        ? WorkspaceNotebookContextMenu
        : WorkspaceFilesContextMenu;
      fileDataObj = setWorkspaceFileInfo(
        workspaceFile._source.entity_name,
        workspaceFile._source.modified_date,
        fileType,
        workspaceFile._source.size,
        undefined,
        fileIcon,
        isUserViewOnly ? ViewOnlyWSItemContextMenu : wsContextMenu,
        (workspaceFile._source.links.self) || undefined,
      );
      break;
    }
    case FileType.RUNS: {
      fileDataObj = setWorkspaceFileInfo(
        workspaceFile._source.entity_name,
        workspaceFile._source.modified_date,
        'Analysis',
        workspaceFile._source.size,
        workspaceFile._source.created_date,
        WORKSPACE_CONTENT_ICONS.analysisIcon,
        isUserViewOnly ? [] : WorkspaceAnalysisContextMenu,
        (workspaceFile._source.links.self) || undefined,
      );
      break;
    }
    default: {
      throwError('Invalid file type');
      break;
    }
  }
  return fileDataObj;
}

/**
 * This function sets the name, date and type of the workspace content
 */
function setWorkspaceFileInfo(
  name: string,
  date: number | undefined,
  type: string,
  size: number,
  createdOn: number | undefined,
  icon: string,
  menuOptions: MenuItem[],
  selfLink?: string,
): FileData {
  return {
    fileName: name,
    fileDate: date,
    fileType: type,
    size,
    created_on: createdOn,
    icon,
    wsItemContextMenu: menuOptions,
    selfLink
  };
}

/**
 * This function calculates the total number of files of a particular file type.
 *
 * @deprecated This function is deprecated in terms with using newer ElasticSearch APIs
 */
export function getWorkspaceContentMetaData(contents: RootApiResourceListResponseInterface<any>[]): WorkspaceContentMetaData[] {
  const metaData: WorkspaceContentMetaData[] = [];
  let analysisCount: number = 0;
  contents.forEach((content: RootApiResourceListResponseInterface<any>): void => {
    if (Array.isArray(content.data) && content.data.length > 0 && content.meta && content.meta.total && typeof(content.meta.total) === 'number') {
      if ([FileType.RUNS].includes(content.data[0].type)) {
        analysisCount += content.meta.total;
      } else {
        metaData.push({
          type: isFile(content.data[0].type) ? 'files' : content.data[0].type,
          total: content.meta.total,
        });
      }
    }
  });
  if (analysisCount > 0) {
    metaData.push({
      type: 'analyses',
      total: analysisCount,
    });
  }

  return metaData;
}

/**
 * @description Converts the ES response for content metadata to the one used in UI component
 *
 * @param contents Metadata contents from ElasticSearch
 * @return WorkspaceContentMetaData[]
 */
export function getWorkspaceContentMetaDataFromES(contents: ElasticCountApiResponse[]): WorkspaceContentMetaData[] {
  const metaData: WorkspaceContentMetaData[] = [];
  if (contents[0].count === 0 && contents[1].count === 0) {
    return metaData;
  }
  metaData.push({
    type: contents[0].count > 1 ? 'analyses' : 'analysis',
    total: contents[0].count,
  });
  metaData.push({
    type: contents[1].count > 1 ? 'files' : 'file',
    total: contents[1].count,
  });
  metaData.push({
    type: contents[2].count > 1 ? 'folders' : 'folder',
    total: contents[2].count,
  });
  return metaData;
}

/**
 * This function evaluates whether the reference file is currently selected or is a part of the files selected through checkboxes
 * @param wsFile is the reference file
 * @param currentSelectedFile is the file which is currently selected
 * @param wsSelectedFiles this is an array of selected files which are selected using checkboxes
 * @param checkCurrentSelectedFile If we need to check current single selected file or not
 */
export function isFileSelected(
  wsFile: Hit<WorkspaceContentItem>,
  currentSelectedFile: Hit<WorkspaceContentItem> | undefined,
  wsSelectedFiles: Hit<WorkspaceContentItem>[],
  checkCurrentSelectedFile: boolean,
): boolean {
  const matchedSelectedFile = wsSelectedFiles.find((i) => i._id === wsFile._id);
  const matchedSelectedFileId = matchedSelectedFile && matchedSelectedFile._id;
  if (currentSelectedFile && checkCurrentSelectedFile) {
    return wsFile._id === currentSelectedFile._id || wsFile._id === matchedSelectedFileId;
  } else {
    return wsFile._id === matchedSelectedFileId;
  }
}

export function convertToCommonWSFile(wsFiles: Hit<WorkspaceContentItem>[]): WorkspaceFile[] {
  const workspaceFiles: WorkspaceFile[] = [];
  wsFiles.forEach((wsFile: Hit<WorkspaceContentItem>) => {
    const fileData: FileData | undefined = getFileData(wsFile);
    if (fileData) {
      const workspaceFile: WorkspaceFile = {
        action: wsFile._source.operation,
        fileName: fileData.fileName,
        fileType: fileData.fileType,
        fileSize: fileData.size,
        lastModified: fileData.fileDate,
        createdOn: fileData.created_on,
        originalFile: wsFile,
      };
      workspaceFiles.push(workspaceFile);
    }
  });
  return workspaceFiles;
}

/**
 * @description Get the file extension by using substring() method on fileName
 *
 * @param fileName name of the file
 * @return string file extension
 */
export function getFileExtension(fileName: string): string {
  // if there is no extension present in fileName
  if (!fileName.includes('.')) return '';

  const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length);
  // return the file extension mapped in WorkspaceDetailFileTypes constant
  return WorkspaceDetailFileTypes.hasOwnProperty(fileExtension) ? WorkspaceDetailFileTypes[fileExtension] : fileExtension;
}

/**
 * @description Get the file name from path
 *
 * @param path path of the file
 * @return string file name
 */
export function getFileName(path: string): string {
  if (!path.includes('/')) return path;

  return path.substring(path.lastIndexOf('/') + 1, path.length);
}

/**
 * @description Removes specific workspace from list, used in deleting, restoring and archiving the workspace
 *
 * @param workspaceList from where workspace needs to be removed
 * @param deleteWorkspaceId ID of the workspace to be deleted
 * @return WorkspaceListResponse
 */
export function removeWorkspaceFromList(
  workspaceList: ElasticApiResponse<ESWorkspaceAttributes, undefined>,
  deleteWorkspaceId: string
): ElasticApiResponse<ESWorkspaceAttributes, undefined> {
  for (let index = 0; index < workspaceList.hits.hits.length; index++) {
    if (workspaceList.hits.hits[index]._id === deleteWorkspaceId) {
      workspaceList.hits.hits.splice(index, 1);
      break;
    }
  }
  return workspaceList;
}

export function isFile(fileType: string): boolean {
  return [FileType.FILE, FileType.FOLDER, FileType.NOTEBOOK].includes(fileType);
}

/**
 * @description remove the workspaceID from the url
 * @param path of the page
 * @returns remaining string after removing the workspaceID
 */
export function removeWorkspaceIdFromPath(path: string): string {
  return path.substring(path.indexOf('/') + 1);
}

/**
 * @description Converts the JSON:API Resource object structure to ES Resource being used for workspace contents
 *
 * @param resource JSON:API Resource object
 * @param workspaceId Unique ID of the workspace
 */
export function convertJsonApiResourceToESResource(resource: ApiResourceInterface, workspaceId: string = ''): Hit<WorkspaceContentItem> {
  return {
    _source: {
      entity_type: resource.type,
      entity_name: resource.attributes.file_name,
      modified_date: new Date(resource.attributes.last_modified).getTime() / 1000,
      s3_key: resource.attributes.s3_key,
      is_pinned: resource.attributes.is_pinned || false,
      workspace_id: workspaceId,
      size: resource.attributes.size,
      links: {
        self: resource.links && resource.links.self || '',
      },
      parent_id: '',
      report_id: resource.attributes.report_id,
    },
    _id: resource.id,
    _index: '',
    _score: 0,
    _type: resource.type,
    sort: []
  };
}

/**
 * @description Generates payload for generating JWT token to access HTML Report file
 *
 * @param s3Key S3 Key for request Report file
 * @param reportId Unique report ID attached with file
 * @return ReportFileTokenRequestInterface
 */
export function generateReportTokenPayload(s3Key: string, reportId: string): ReportFileTokenRequestInterface {
  return {
    data: {
      type: 'workspaces',
      attributes: {
        s3_key: s3Key,
        report_id: reportId,
      },
    },
  };
}

export function isFileAllowedToUpload(fileName: string): boolean {
  const lowerCaseName: string = fileName.toLowerCase();
  return FILE_UPLOAD_TYPES.some((type: string): boolean => lowerCaseName.endsWith(type.toLowerCase()));
};
